import React, { useState } from 'react';
import {
  Button,
  Col,
  Collapse,
  Form,
  Layout,
  Modal,
  notification,
  Popconfirm,
  Row,
  Select,
  Space,
  Tooltip,
  Typography,
} from 'antd';
import {
  CopyFilled,
  DeleteFilled,
  EditFilled,
  PlusSquareFilled,
  QuestionCircleFilled,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import BreadcrumbContainer from '../../../containers/Breadcrumb';
import { CustomSelector } from '../../../components';
import AjaxTable from '../../../components/AjaxTable';
import ModalForm from '../../../components/ModalForm';
import {
  activateForm,
  ajaxTableForms as filterForms,
  createForm,
  deactivateForm,
  getCategories,
  getProjectsByStatus,
  updateForm,
} from '../../../api/module/forms';
import MESSAGES from '../../../enums/Messages';
import {
  catchException,
  getLocalStorage,
} from '../../../utils/helpers/helpers';
import INTERNAL_LINKS from '../../../enums/InternalLinks';

const { Content } = Layout;
const { Title } = Typography;

export default function AdministrationFormsList({history}) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [openFormModal, setOpenFormModal] = useState(false);
  const [formFilter, setFormFilter] = useState(undefined);
  const [isEditing, setIsEditing] = useState(false);
  const [isClone, setIsClone] = useState(false);
  const [data, setData] = useState({});
  const [cloneData, setCloneData] = useState({});
  const [reloadForms, setReloadForms] = useState(false);
  const [userId] = useState(getLocalStorage('userId'));

  const breadcrumbItems = [
    {
      text: 'Formularios - Administración',
    },
  ];

  const columnsForms = [
    {
      title: 'Cod',
      dataIndex: 'codigo',
      key: 'codigo',
    },
    {
      title: 'Proyecto',
      dataIndex: 'proyecto_nombre',
      key: 'proyecto_nombre',
    },
    {
      title: 'Formulario',
      dataIndex: 'nombre',
      key: 'nombre',
    },
    {
      title: 'Categoría',
      dataIndex: 'categoria_nombre',
      key: 'categoria_nombre',
    },
    {
      title: 'F. Padre',
      dataIndex: 'formulario_padre_nombre',
      key: 'formulario_padre_nombre',
      align: 'center',
      render: (text) => text || '-',
    },
    {
      title: 'Múltiple',
      dataIndex: 'multiple',
      key: 'multiple',
      align: 'center',
      render: (text) => (text ? 'Sí' : 'No'),
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      align: 'center',
      render: (text) => (text ? 'A' : 'I'),
    },
    {
      title: 'Acciones',
      dataIndex: 'accion',
      align: 'center',
      fixed: 'right',
      width: 125,
      render: (_, record) => (
        <Space>
          <Tooltip title='Editar Formulario' placement='top'>
            <EditFilled
              onClick={() => handleEdit(record)}
              disabled={record.usuario !== userId}
              style={{ fontSize: '16px', color: 'green' }}
            />
          </Tooltip>
          <Tooltip title='Administrar Preguntas' placement='top'>
            <QuestionCircleFilled
              onClick={() => handleQuestion(record.id)}
              style={{ fontSize: '16px', color: 'blue' }}
            />
          </Tooltip>
          { record.estado && (
            <Tooltip title='Desactivar Formulario' placement='top'>
              <Popconfirm
                title="Desactivar Formulario"
                description='Está seguro de desactivar el formulario ?'
                onConfirm={async () => {
                  try {
                    await deactivateForm(record.id);
                    notification.success({
                      message: MESSAGES.SUCCESS_MSG,
                      description: MESSAGES.DESC_SUCCESS_MSG,
                      duration: 2,
                    });
                    setReloadForms({});
                  } catch (error) {
                    catchException(error);
                  }
                }}
                icon={<WarningOutlined style={{ color: 'red' }} />}
              >
                <DeleteFilled
                  style={{ fontSize: '16px', color: 'red' }}
                />
              </Popconfirm>
            </Tooltip>
          )}
          { record.estado === false && (
            <Tooltip title='Reactivar Formulario' placement='top'>
              <Popconfirm
                title="Reactivar Formulario"
                description='Está seguro de reactivar el formulario ?'
                onConfirm={async () => {
                  try {
                    await activateForm(record.id);
                    notification.success({
                      message: MESSAGES.SUCCESS_MSG,
                      description: MESSAGES.DESC_SUCCESS_MSG,
                      duration: 2,
                    });
                    setReloadForms({});
                  } catch (error) {
                    catchException(error);
                  }
                }}
                icon={<WarningOutlined style={{ color: 'orange' }} />}
              >
                <PlusSquareFilled
                  style={{ fontSize: '16px', color: 'orange' }}
                />
              </Popconfirm>
            </Tooltip>
          )}
          <Tooltip title='Clonar Formulario' placement='top'>
            <CopyFilled
              onClick={() => handleCopy(record)}
              style={{ fontSize: '16px', color: 'blue' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleEdit = async (record) => {
    setData(record);
    setIsEditing(true);
    setIsClone(false);
    setOpenFormModal(true);
  };

  const handleQuestion = (id) => {
    history.push(`${INTERNAL_LINKS.FORM_QUESTIONS}/${id}`)
  };

  const handleCopy = (record) => {
    setData(record);
    setCloneData(record);
    setIsEditing(false);
    setIsClone(true);
    setOpenFormModal(true);
  };

  // TODO: el query se lo puede pasar al AjaxTable como objeto y de ahi al endpoint como {params:query}
  const onFinish = async (values) => {
    setFormFilter(
      Object.entries(values).reduce((pv, cv) => {
        pv[`filter[${cv[0]}]`] = cv[1];
        return pv;
      }, {}),
    );
  };

  // TODO: Agregar aqui que si isClone es true, se debe tambien clonar sus preguntas
  const onFinishModal = async (values) => {
    setLoading(true);
    try {
      if (isEditing) {
        await updateForm(data.id, values);
        notification.success({
          message: MESSAGES.SUCCESS_MSG,
          description: MESSAGES.DESC_SUCCESS_MSG,
          duration: 2,
        });
      } else {
        await createForm(values);
        notification.success({
          message: MESSAGES.SUCCESS_MSG,
          description: MESSAGES.DESC_SUCCESS_MSG,
          duration: 2,
        });
      }
    } catch (error) {
      catchException(error);
    } finally {
      handleResetModal();
      setReloadForms({});
      setLoading(false);
    }
  };

  const handleReset = () => {
    form.resetFields();
    setFormFilter(undefined);
  };

  const handleResetModal = () => {
    setData({});
    setCloneData({});
    setIsEditing(false);
    setIsClone(false);
    setOpenFormModal(false);
  };

  return (
    <Content>
      <Row>
        <Col span={24}>
          <BreadcrumbContainer items={breadcrumbItems} />
        </Col>
        <Col span={24}>
          <Title className={'text-uppercase'} level={2}>
            Formularios - Administración
          </Title>
        </Col>
      </Row>
      <Row style={{ marginTop: '20px' }}>
        <Col span={24}>
          <Collapse items={[
            {
              key: '1',
              label: <Title level={5}>Búsqueda de formularios</Title>,
              children:
                <Form
                  form={form}
                  name="forms_search_form"
                  layout="vertical"
                  onFinish={onFinish}
                >
                  <Row style={{ marginTop: '10px' }} justify="space-between">
                    <Col span={6}>
                      <Form.Item
                        label="Estado Proyecto"
                        name="estado_proyecto"
                        placeholder="Activos / Inactivos / Todos"
                        rules={[
                          {
                            required: true,
                          },
                        ]}
                      >
                        <Select>
                          <Select.Option value="1">Activo</Select.Option>
                          <Select.Option value="2">Inactivo</Select.Option>
                          <Select.Option value="3">Todos</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={14}>
                      <Form.Item noStyle dependencies={['estado_proyecto']}>
                        {({ getFieldValue }) => (
                          <Form.Item
                            label="Proyecto:"
                            name="proyecto"
                            placeholder="Proyectos"
                            valuePropName="initialValue"
                          >
                            <CustomSelector
                              selectorProps={{
                                style: { width: '100%' },
                                placeholder: 'Proyectos',
                              }}
                              endpointFetch={async () => {
                                const statusProject =
                                  getFieldValue('estado_proyecto');
                                if (!statusProject) {
                                  return { data: { results: [] } }; // Retorna un objeto con un arreglo vacío
                                }
                                return await getProjectsByStatus(statusProject);
                              }}
                              key={getFieldValue('estado_proyecto')}
                              disabled={!getFieldValue('estado_proyecto')}
                            />
                          </Form.Item>
                        )}
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '10px' }} justify="space-between">
                    <Col span={6}>
                      <Form.Item
                        label="Estado Formulario:"
                        name="estado"
                        placeholder="Activos / Inactivos / Todos"
                      >
                        <Select>
                          <Select.Option value="1">Activo</Select.Option>
                          <Select.Option value="0">Inactivo</Select.Option>
                          <Select.Option value="2">Todos</Select.Option>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col span={14}>
                      <Form.Item
                        label="Categoría:"
                        name="categoria"
                        placeholder="Categorías"
                        valuePropName="initialValue"
                      >
                        <CustomSelector
                          selectorProps={{
                            style: { width: '100%' },
                            placeholder: 'Categorías',
                          }}
                          endpointFetch={async () => {
                            return await getCategories();
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Form.Item wrapperCol={{ span: 24 }}>
                    <Button
                      type="primary"
                      icon={<SearchOutlined />}
                      htmlType="submit"
                    >Buscar</Button>
                    <Button
                      style={{ marginLeft: '20px' }}
                      danger
                      onClick={() => handleReset()}
                    >Borrar filtros</Button>
                    <Button
                      style={{ marginLeft: '20px' }}
                      type="default"
                      onClick={() => setOpenFormModal(true)}
                    >+ Crear nuevo formulario</Button>
                  </Form.Item>
                </Form>,
            }
          ]}/>
        </Col>
      </Row>
      <Row style={{ marginTop: '20px' }}>
        <Col span={24}>
          <AjaxTable
            tableTitle={'Listado de formularios'}
            rowKey={'id'}
            endpoint={filterForms}
            query={formFilter}
            columns={columnsForms}
            searchInput={true}
            reloadSource={reloadForms}
          />
        </Col>
      </Row>

      <Modal
        title={
          isClone
            ? 'Formulario clonar'
            : !isEditing
            ? 'Formulario creación'
            : 'Formulario edición'
        }
        open={openFormModal}
        onCancel={handleResetModal}
        footer={null}
        width={'80%'}
        destroyOnClose
      >
        <ModalForm
          onFinish={onFinishModal}
          loading={loading}
          isEditing={isEditing}
          isClone={isClone}
          data={data}
          cloneData={cloneData}
          setReloadForms={setReloadForms}
          onCancel={handleResetModal}
        />
      </Modal>
    </Content>
  );
}
